import { randomString } from '@chronosphereio/core';
import { produce } from 'immer';
import { useMemo } from 'react';
import { ChronosphereDataQueryDefinition, PanelDefinition } from '@/core';
import { useExperimentalProcessQueryDefinitions, useReplaceVariablesInString } from '@/plugin-system';
import {
  GRID_LAYOUT_COLS,
  GRID_LAYOUT_SMALL_BREAKPOINT,
  SPLIT_VARIABLE_PANEL_KEY_DELIMITER,
  SPLIT_VARIABLE_VALUE_DELIMITER,
} from '../constants';
import { PanelGroupItemLayout } from '../context';

/**
 * Get the maximum X coordinate (rightmost column boundary) for the dashboard grid.
 */
export function getGridMaxX(): number {
  return GRID_LAYOUT_COLS[GRID_LAYOUT_SMALL_BREAKPOINT];
}

type PanelGroupItemBounds = {
  /**
   * Left horizontal position.
   */
  x1: number;
  /**
   * Right horizontal position.
   */
  x2: number;
  /**
   * Top vertical position.
   */
  y1: number;
  /**
   * Bottom vertical position
   */
  y2: number;
};

function getPanelBounds({ x, y, w, h }: PanelGroupItemLayout): PanelGroupItemBounds {
  return {
    x1: x,
    x2: x + w,
    y1: y,
    y2: y + h,
  };
}

export type UnpositionedPanelGroupItemLayout = Omit<PanelGroupItemLayout, 'x' | 'y'>;

/**
 * Inserts a new panel into the layout with placement determined by a specified
 * reference panel. The new panel is placed:
 * - To the right of the reference panel if there is space available without
 *   moving other panels.
 * - Otherwise, directly below the reference panel. If other panels are below
 *   this location, they will also shift downward because the grid uses
 *   vertical-based compacting.
 *
 * @param newLayout - Layout for new panel to insert into the grid.
 * @param referenceLayout - Layout for reference panel used to determine the
 *   placement of the new panel.
 * @param itemLayouts - Full grid layout.
 * @returns - Item layouts modified to insert the new panel.
 */
export function insertPanelInLayout(
  newLayout: UnpositionedPanelGroupItemLayout,
  referenceLayout: PanelGroupItemLayout,
  itemLayouts: PanelGroupItemLayout[]
): PanelGroupItemLayout[] {
  const maxX = getGridMaxX();

  const referenceBounds = getPanelBounds(referenceLayout);

  // Organize layouts based on vertical relation to the item being inserted
  // after.
  const aboveInsertRow: PanelGroupItemLayout[] = [];
  const insertRow: PanelGroupItemLayout[] = [];
  const belowInsertRow: PanelGroupItemLayout[] = [];
  itemLayouts.forEach((itemLayout) => {
    const itemBounds = getPanelBounds(itemLayout);

    if (itemBounds.y2 <= referenceBounds.y1) {
      aboveInsertRow.push(itemLayout);
    } else if (itemBounds.y1 >= referenceBounds.y2) {
      belowInsertRow.push(itemLayout);
    } else {
      insertRow.push(itemLayout);
    }
  });

  // Cannot safely assume that the order of item layouts array is strictly
  // left to right. Sorting the row by horizontal position to more easily find
  // gaps.
  insertRow.sort((a, b) => a.x - b.x);
  const insertAfterIndex = insertRow.findIndex((item) => item.i === referenceLayout.i);

  if (insertAfterIndex === insertRow.length - 1) {
    // Insert to the right when space is available and the reference is the last
    // item in the row.
    if (referenceBounds.x2 + newLayout.w <= maxX) {
      return [
        ...aboveInsertRow,
        ...insertRow,
        {
          ...newLayout,
          x: referenceBounds.x2,
          y: referenceBounds.y1,
        },
        ...belowInsertRow,
      ];
    }
  } else if (insertAfterIndex >= 0) {
    const nextItem = insertRow[insertAfterIndex + 1];

    if (nextItem && getPanelBounds(nextItem).x1 - referenceBounds.x2 >= newLayout.w) {
      // Insert to the right when space is available between the reference and
      // the next item in the row.
      insertRow.splice(insertAfterIndex + 1, 0, {
        ...newLayout,
        x: referenceBounds.x2,
        y: referenceBounds.y1,
      });

      return [...aboveInsertRow, ...insertRow, ...belowInsertRow];
    }
  }

  // Insert the new item below the original and shift the items below the
  // row where the reference is located.
  return [
    ...aboveInsertRow,
    ...insertRow,
    { x: referenceBounds.x1, y: referenceBounds.y2, ...newLayout },
    ...belowInsertRow.map((itemLayout) => {
      // Note: the grid will not necessarily display all of these items shifted
      // all the way down because of vertical compacting, but shifing their
      // y position ensures the new item gets vertical precedence over items
      // below it in that compacting.
      return { ...itemLayout, y: itemLayout.y + newLayout.h };
    }),
  ];
}

/**
 * Get a unique panel key using a random id as the suffix.
 */
export function getUniquePanelKey({
  panels,
  numRandomCharacters = 8,
}: {
  panels: Record<string, PanelDefinition>;
  numRandomCharacters?: number;
}): string {
  let randomId = randomString(numRandomCharacters);
  // Ensure randomId is unique by checking against the existing panels
  while (randomId in panels) {
    randomId = randomString(numRandomCharacters);
  }
  return randomId;
}

/**
 * Helper hook to process the panel definition and interpolate the query definitions.
 */
export function useProcessPanelDefinition(props: { panelDefinition: PanelDefinition }) {
  const { panelDefinition } = props;

  const result = useExperimentalProcessQueryDefinitions(panelDefinition.spec.queries?.map((q) => q.spec.plugin) ?? []);
  const displayName = useReplaceVariablesInString(panelDefinition.spec.display.name);
  return useMemo(() => {
    const queriesProcessed = result.data?.map((d) => {
      return d.definition;
    });

    return {
      panelDefinition,
      interpolatedPanelDefinition: produce(panelDefinition, (draft) => {
        if (displayName) {
          draft.spec.display.name = displayName;
        }
        draft.spec.queries?.forEach((q, idx) => {
          const query = queriesProcessed?.[idx];
          if (query) {
            q.spec.plugin = query as ChronosphereDataQueryDefinition;
          }
        });
      }),
    };
  }, [panelDefinition, result.data, displayName]);
}

/**
 * Find the best spot in the current grid layout to place the next panel.
 * Uses a grid-based algorithm that:
 * 1. Maps all occupied cells from existing panels
 * 2. Scans top-to-bottom, left-to-right for the first available rectangle
 * 3. Returns the position for compact, deterministic panel placement
 */
export function findNextAvailablePosition(
  itemLayouts: PanelGroupItemLayout[],
  newItemWidth: number,
  newItemHeight: number
): { x: number; y: number } {
  if (itemLayouts.length === 0) {
    return { x: 0, y: 0 };
  }

  // Use Set to track occupancy, occupiedCells.has('1,2') means cell (1, 2) is occupied
  const occupiedCells = new Set<string>();
  let maxY = 0;

  // Build occupancy map of all currently occupied grid cells
  itemLayouts.forEach((item) => {
    maxY = Math.max(maxY, item.y + item.h);
    for (let y = item.y; y < item.y + item.h; y++) {
      for (let x = item.x; x < item.x + item.w; x++) {
        occupiedCells.add(`${x},${y}`);
      }
    }
  });

  const maxX = getGridMaxX();

  // Scan grid from top-left to find first available rectangle of required size
  for (let y = 0; y < maxY + newItemHeight; y++) {
    for (let x = 0; x <= maxX - newItemWidth; x++) {
      let isOccupied = false;

      // Check if the entire rectangle would fit without overlapping existing panels
      for (let checkY = y; checkY < y + newItemHeight && !isOccupied; checkY++) {
        for (let checkX = x; checkX < x + newItemWidth; checkX++) {
          if (occupiedCells.has(`${checkX},${checkY}`)) {
            isOccupied = true;
            break;
          }
        }
      }

      if (!isOccupied) {
        return { x, y };
      }
    }
  }

  // Fallback: place at bottom-left if no space found
  return { x: 0, y: maxY };
}

/**
 * Extracts the base panel key from a composite key.
 * Composite keys include encoded split variable info.
 */
export function extractBasePanelKey(compositePanelKey?: string): string | undefined {
  if (!compositePanelKey) return undefined;
  const panelKeyEndIndex = compositePanelKey.indexOf(SPLIT_VARIABLE_PANEL_KEY_DELIMITER);
  if (panelKeyEndIndex !== -1) {
    return compositePanelKey.substring(0, panelKeyEndIndex);
  }
  return compositePanelKey;
}

const EMPTY_SPLIT_VARIABLE_INFO = {
  splitVariableName: undefined,
  splitVariableValue: undefined,
};

/**
 * Extracts the split variable info from a composite key.
 */
export function extractSplitVariableInfo(compositePanelKey?: string) {
  if (!compositePanelKey) return EMPTY_SPLIT_VARIABLE_INFO;

  // Count occurrences of each delimiter in case user defined variable happens to contain the delimiter
  const nameDelimiterCount = compositePanelKey.split(SPLIT_VARIABLE_PANEL_KEY_DELIMITER).length - 1;
  const valueDelimiterCount = compositePanelKey.split(SPLIT_VARIABLE_VALUE_DELIMITER).length - 1;
  // If multiple instances of either delimiter are found, return empty info to fallback gracefully
  if (nameDelimiterCount > 1 || valueDelimiterCount > 1) {
    return EMPTY_SPLIT_VARIABLE_INFO;
  }

  const panelKeyEndIndex = compositePanelKey.indexOf(SPLIT_VARIABLE_PANEL_KEY_DELIMITER);
  const splitVariableValueIndex = compositePanelKey.indexOf(SPLIT_VARIABLE_VALUE_DELIMITER);
  if (panelKeyEndIndex !== -1 && splitVariableValueIndex !== -1) {
    return {
      splitVariableName: compositePanelKey.substring(
        panelKeyEndIndex + SPLIT_VARIABLE_PANEL_KEY_DELIMITER.length,
        splitVariableValueIndex
      ),
      splitVariableValue: compositePanelKey.substring(splitVariableValueIndex + SPLIT_VARIABLE_VALUE_DELIMITER.length),
    };
  }
  return EMPTY_SPLIT_VARIABLE_INFO;
}
